import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getDocumentInfoGrpc } from '../api/grpc/validationGrpcClient';
import { subscribeToRevitEvents } from '../api/grpc/revitClient';
import { isGrpcRuntimeAvailable } from '../api/grpc/transport';
import { useHostWindowVisibility } from './useHostWindowVisibility';
import {
  invalidateRevitModelCaches,
  parseRevitModelInvalidationEvent,
  type RevitModelInvalidation,
} from '../lib/revit/modelCacheInvalidation';

const FALLBACK_POLL_INTERVAL_MS = 60_000;
export const E2E_ACTIVE_DOCUMENT_NAME = 'Demo-Piping-Model.rvt';
export const E2E_ACTIVE_DOCUMENT_PATH = 'C:\\Projects\\Demo\\Demo-Piping-Model.rvt';

export interface ActiveDocumentInfo {
  documentName: string | null;
  documentPath: string | null;
  projectKey: string | null;
  modelVersion: number | null;
  lastChangedAt: number;
}

export const EMPTY_ACTIVE_DOCUMENT_INFO: ActiveDocumentInfo = {
  documentName: null,
  documentPath: null,
  projectKey: null,
  modelVersion: null,
  lastChangedAt: 0,
};

export interface ActiveDocumentRefreshState {
  /** True when Revit reported model changes that the UI has not adopted yet. */
  modelDataStale: boolean;
  /** Version offered by the latest unadopted ModelChanged event (null for unsaved docs). */
  pendingModelVersion: number | null;
  /** Adopts pending model changes: invalidates caches and bumps the adopted version. */
  refreshModelData: () => void;
  /** Adopts an explicit version, e.g. returned by a successful mutating call. */
  adoptModelVersion: (modelVersion: number | null) => void;
}

/** Inert default for consumers rendered outside ActiveDocumentProvider (tests). */
export const INERT_ACTIVE_DOCUMENT_REFRESH: ActiveDocumentRefreshState = {
  modelDataStale: false,
  pendingModelVersion: null,
  refreshModelData: () => {},
  adoptModelVersion: () => {},
};

function normalizeDocumentIdentityPart(value: string | null | undefined): string | null {
  const normalized = value?.trim().replace(/\//g, '\\');
  return normalized ? normalized : null;
}

export function resolveActiveProjectKey(input: {
  documentName?: string | null;
  documentPath?: string | null;
}): string | null {
  const documentPath = normalizeDocumentIdentityPart(input.documentPath);
  if (documentPath) {
    return documentPath.toLowerCase();
  }

  const documentName = normalizeDocumentIdentityPart(input.documentName);
  return documentName ? `name:${documentName.toLowerCase()}` : null;
}

function createActiveDocumentInfo(
  documentName: string | null | undefined,
  documentPath: string | null | undefined,
  previous: ActiveDocumentInfo,
  modelVersion: number | null | undefined = previous.modelVersion
): ActiveDocumentInfo {
  const normalizedName = normalizeDocumentIdentityPart(documentName);
  const normalizedPath = normalizeDocumentIdentityPart(documentPath);
  const projectKey = resolveActiveProjectKey({
    documentName: normalizedName,
    documentPath: normalizedPath,
  });

  if (
    previous.documentName === normalizedName &&
    previous.documentPath === normalizedPath &&
    previous.projectKey === projectKey &&
    previous.modelVersion === (modelVersion ?? null)
  ) {
    return previous;
  }

  if (!projectKey && previous.projectKey) {
    console.warn('[useActiveDocumentInfo] Active project cleared (was: %s)', previous.projectKey);
  }

  return {
    documentName: normalizedName,
    documentPath: normalizedPath,
    projectKey,
    modelVersion: modelVersion ?? null,
    lastChangedAt: Date.now(),
  };
}

function shouldUseE2eActiveDocumentFallback(): boolean {
  if (
    process.env.REACT_APP_PIPEEX_E2E_BYPASS !== '1' ||
    typeof window === 'undefined'
  ) {
    return false;
  }

  return new URLSearchParams(window.location.search).get('e2e') === '1';
}

function createE2eActiveDocumentFallback(previous: ActiveDocumentInfo): ActiveDocumentInfo | null {
  if (!shouldUseE2eActiveDocumentFallback()) {
    return null;
  }

  return createActiveDocumentInfo(
    E2E_ACTIVE_DOCUMENT_NAME,
    E2E_ACTIVE_DOCUMENT_PATH,
    previous,
    previous.modelVersion ?? 1
  );
}

function isSameDocumentIdentity(
  current: ActiveDocumentInfo,
  next: { documentName?: string | null; documentPath?: string | null }
): boolean {
  const nextName = normalizeDocumentIdentityPart(next.documentName);
  const nextPath = normalizeDocumentIdentityPart(next.documentPath);
  const nextProjectKey = resolveActiveProjectKey({
    documentName: nextName,
    documentPath: nextPath,
  });

  if (nextProjectKey && current.projectKey === nextProjectKey) {
    return true;
  }

  return Boolean(nextName && current.documentName === nextName);
}

export function shouldIgnoreInactiveDocumentEvent(
  current: ActiveDocumentInfo,
  invalidation: NonNullable<ReturnType<typeof parseRevitModelInvalidationEvent>>
): boolean {
  if (!current.projectKey) {
    return false;
  }

  const isCurrentDocument = isSameDocumentIdentity(current, invalidation);
  if (invalidation.eventType === 'DocumentClosed') {
    return isCurrentDocument;
  }

  if (isCurrentDocument) {
    return false;
  }

  // DocumentOpened is NOT ignored: the plugin filters family/linked documents
  // before publishing, so a project DocumentOpened is a document Revit just
  // made active. Dropping it strands the UI on the previous project (the
  // plugin also dedups the follow-up ViewActivated), and the profile/repair
  // gate never reappears after opening another project.
  return invalidation.eventType === 'DocumentClosing' ||
    invalidation.eventType === 'ModelChanged';
}

function isInvalidationAlreadyCurrent(
  current: ActiveDocumentInfo,
  invalidation: NonNullable<ReturnType<typeof parseRevitModelInvalidationEvent>>
): boolean {
  if (invalidation.eventType === 'ModelChanged') {
    // Unsaved documents have no version to compare; treating them as "already
    // current" would suppress every cache invalidation after the first one.
    if (invalidation.modelVersion === null) {
      return false;
    }

    return isSameDocumentIdentity(current, invalidation) &&
      current.modelVersion === invalidation.modelVersion;
  }

  if (invalidation.eventType === 'DocumentOpened') {
    // Reopening the same path creates a new Revit document generation and may
    // follow a file-format upgrade, so its model-derived caches are never current.
    return false;
  }

  if (invalidation.eventType === 'DocumentChanged') {
    return isSameDocumentIdentity(current, invalidation);
  }

  if (invalidation.eventType === 'DocumentClosed') {
    return !current.projectKey;
  }

  return false;
}

/**
 * Resolves the active Revit document identity for UI chrome and project-scoped workflow state.
 */
export function useActiveDocumentInfoState(): {
  documentInfo: ActiveDocumentInfo;
  refreshState: ActiveDocumentRefreshState;
} {
  const hostWindowVisible = useHostWindowVisibility();
  const [documentInfo, setDocumentInfo] = useState<ActiveDocumentInfo>(
    () => createE2eActiveDocumentFallback(EMPTY_ACTIVE_DOCUMENT_INFO) ?? EMPTY_ACTIVE_DOCUMENT_INFO
  );
  const [fallbackPollingEnabled, setFallbackPollingEnabled] = useState(false);
  const documentInfoRef = useRef(documentInfo);
  // Plugin-side open generation, tracked from event payloads. A snapshot whose
  // generation advanced while this client was disconnected means a document
  // transition was missed: per-generation session state must be dropped.
  const documentGenerationRef = useRef<number | null>(null);
  // ModelChanged events no longer invalidate or refetch automatically: the
  // offered version is parked as "pending" until a page-level Refresh action
  // (or a full UI reopen, which re-probes the current version) adopts it.
  const [pendingModelVersion, setPendingModelVersion] = useState<number | null>(null);
  const [modelDataStale, setModelDataStale] = useState(false);
  const pendingModelInvalidationRef = useRef<RevitModelInvalidation | null>(null);

  useEffect(() => {
    documentInfoRef.current = documentInfo;
  }, [documentInfo]);

  const updateDocumentInfo = useCallback((
    name: string | null,
    path: string | null = null,
    modelVersion?: number | null
  ) => {
    setDocumentInfo((prev) => createActiveDocumentInfo(name, path, prev, modelVersion));
  }, []);

  const fetchDocumentInfo = useCallback(async () => {
    if (!hostWindowVisible) {
      return;
    }

    if (!isGrpcRuntimeAvailable()) {
      const fallback = createE2eActiveDocumentFallback(documentInfoRef.current);
      if (fallback) {
        setDocumentInfo(fallback);
      }
      return;
    }

    try {
      const result = await getDocumentInfoGrpc();
      if (!result.success) {
        const fallback = createE2eActiveDocumentFallback(documentInfoRef.current);
        if (fallback) {
          setDocumentInfo(fallback);
          return;
        }

        console.warn(
          '[useActiveDocumentInfo] Retaining active project after document-info probe failed:',
          result.error ?? 'Unknown error'
        );
        setFallbackPollingEnabled(true);
        return;
      }

      setFallbackPollingEnabled(false);
      updateDocumentInfo(result.documentName ?? null, result.documentPath ?? null);
    } catch (error) {
      const fallback = createE2eActiveDocumentFallback(documentInfoRef.current);
      if (fallback) {
        setDocumentInfo(fallback);
        return;
      }

      console.warn(
        '[useActiveDocumentInfo] Retaining active project after document-info probe threw:',
        error
      );
      setFallbackPollingEnabled(true);
    }
  }, [hostWindowVisible, updateDocumentInfo]);

  useEffect(() => {
    if (!hostWindowVisible) {
      return undefined;
    }

    void fetchDocumentInfo();
    return undefined;
  }, [fetchDocumentInfo, hostWindowVisible]);

  const fetchDocumentInfoRef = useRef(fetchDocumentInfo);
  useEffect(() => {
    fetchDocumentInfoRef.current = fetchDocumentInfo;
  }, [fetchDocumentInfo]);

  // The lifecycle stream stays subscribed while the host pane is hidden: the
  // pane outlives documents, and a close/open/save cycle performed while
  // hidden must still reset session state and update repair persistence —
  // otherwise a stale profile confirmation suppresses the gate on re-show.
  // Data probes below remain visibility-gated. The subscription is stable
  // across visibility flips: tearing it down mid-toggle would drop the very
  // close/open events this hook exists to observe.
  useEffect(() => {
    if (!isGrpcRuntimeAvailable()) {
      return undefined;
    }

    const unsubscribe = subscribeToRevitEvents(
      (eventType, payload) => {
        setFallbackPollingEnabled(false);
        const invalidation = parseRevitModelInvalidationEvent(eventType, payload);
        if (!invalidation) {
          return;
        }

        if (invalidation.eventType === 'DocumentSnapshot') {
          // Authoritative subscribe-time state: the plugin sends this first on
          // every (re)connect. If a generation passed, or the active identity
          // simply differs (e.g. the project was closed without a reopen while
          // the WebView was suspended), the UI missed real transitions — drop
          // per-generation session state exactly as a live DocumentOpened would.
          const generation =
            typeof invalidation.generation === 'number' ? invalidation.generation : null;
          const previousGeneration = documentGenerationRef.current;
          if (generation !== null) {
            documentGenerationRef.current = generation;
          }
          const missedGenerationTransition =
            generation !== null &&
            previousGeneration !== null &&
            generation !== previousGeneration;
          const missedIdentityChange =
            resolveActiveProjectKey(invalidation) !== documentInfoRef.current.projectKey;
          if (missedGenerationTransition || missedIdentityChange) {
            invalidateRevitModelCaches({ ...invalidation, eventType: 'DocumentOpened' });
          }
          updateDocumentInfo(invalidation.documentName, invalidation.documentPath, null);
          void fetchDocumentInfoRef.current();
          return;
        }

        if (typeof invalidation.generation === 'number') {
          documentGenerationRef.current = invalidation.generation;
        }

        if (shouldIgnoreInactiveDocumentEvent(documentInfoRef.current, invalidation)) {
          return;
        }

        if (eventType === 'ModelChanged') {
          // Manual-refresh mode: record the offered version for stale badges and
          // later adoption, but never invalidate caches or bump the adopted
          // version here — pages reload model data only on explicit Refresh.
          if (!isInvalidationAlreadyCurrent(documentInfoRef.current, invalidation)) {
            pendingModelInvalidationRef.current = invalidation;
            setPendingModelVersion(invalidation.modelVersion);
            setModelDataStale(true);
          }
          return;
        }

        if (!isInvalidationAlreadyCurrent(documentInfoRef.current, invalidation)) {
          invalidateRevitModelCaches(invalidation);
        }

        if (eventType === 'DocumentClosing') {
          if (isSameDocumentIdentity(documentInfoRef.current, invalidation)) {
            updateDocumentInfo(null, null);
          }

          // The named document is the one going away — do NOT adopt it as the
          // active document. Just re-probe so we converge on whatever remains.
          void fetchDocumentInfoRef.current();
        } else if (
          eventType === 'DocumentOpened' ||
          eventType === 'DocumentChanged' ||
          eventType === 'DocumentClosed'
        ) {
          // Optimistically adopt the path-aware identity from the event, then
          // re-probe the live active document to confirm (handles the case
          // where closing one of several open documents switches the active).
          updateDocumentInfo(invalidation.documentName, invalidation.documentPath, null);
          void fetchDocumentInfoRef.current();
        }
      },
      () => {
        setFallbackPollingEnabled(true);
      }
    );

    return () => unsubscribe();
  }, [updateDocumentInfo]);

  useEffect(() => {
    if (!hostWindowVisible || !fallbackPollingEnabled) {
      return undefined;
    }

    const timer = window.setInterval(() => {
      void fetchDocumentInfo();
    }, FALLBACK_POLL_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [fallbackPollingEnabled, fetchDocumentInfo, hostWindowVisible]);

  const refreshModelData = useCallback(() => {
    const pending = pendingModelInvalidationRef.current;
    pendingModelInvalidationRef.current = null;
    setPendingModelVersion(null);
    setModelDataStale(false);
    if (pending) {
      invalidateRevitModelCaches(pending);
      updateDocumentInfo(pending.documentName, pending.documentPath, pending.modelVersion);
      return;
    }

    // Nothing pending means the model has not changed since the adopted version;
    // still drop model-derived caches so the next load genuinely re-fetches.
    const current = documentInfoRef.current;
    invalidateRevitModelCaches({
      eventType: 'ModelChanged',
      reason: 'manual-refresh',
      documentName: current.documentName,
      documentPath: current.documentPath,
      modelVersion: current.modelVersion,
    });
  }, [updateDocumentInfo]);

  const adoptModelVersion = useCallback((modelVersion: number | null) => {
    if (modelVersion === null) {
      return;
    }

    const current = documentInfoRef.current;
    if (current.modelVersion === modelVersion) {
      return;
    }

    invalidateRevitModelCaches({
      eventType: 'ModelChanged',
      reason: 'adopt-applied-version',
      documentName: current.documentName,
      documentPath: current.documentPath,
      modelVersion,
    });
    updateDocumentInfo(current.documentName, current.documentPath, modelVersion);
    setPendingModelVersion((pending) =>
      pending !== null && pending <= modelVersion ? null : pending
    );
    setModelDataStale((stale) => {
      const pending = pendingModelInvalidationRef.current;
      if (stale && pending !== null && (pending.modelVersion ?? Infinity) <= modelVersion) {
        pendingModelInvalidationRef.current = null;
        return false;
      }
      return stale;
    });
  }, [updateDocumentInfo]);

  const refreshState = useMemo(() => ({
    modelDataStale,
    pendingModelVersion,
    refreshModelData,
    adoptModelVersion,
  }), [adoptModelVersion, modelDataStale, pendingModelVersion, refreshModelData]);

  return { documentInfo, refreshState };
}

/**
 * Resolves the active Revit document identity for UI chrome and project-scoped workflow state.
 */
export function useActiveDocumentInfo(): ActiveDocumentInfo {
  return useActiveDocumentInfoState().documentInfo;
}

/**
 * Resolves the active Revit document title for UI chrome (gRPC + Revit event stream + poll).
 */
export function useActiveDocumentName(): string | null {
  return useActiveDocumentInfo().documentName;
}
